import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import { friendlyMessage } from '@/lib/errors';
import {
  Button, PageShell, SectionCard, FormSection, Input, RadioGroup, Switch, Skeleton,
  RequiredMark,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { useNoticeQuery, useNoticeMutation } from '../hooks/useNoticesQuery';
import { useNoticeImageUpload } from '../hooks/useNoticeImageUpload';
import type { NoticePayload, NoticeStatus, NoticeTranslation } from '../types/notice';
import { LocaleHtmlTabs } from '../../components/LocaleHtmlTabs';
import { type ContentLocale } from '../../lib/contentLocales';
import { buildLocaleHtmlTranslations, pickLocaleHtml } from '../../lib/localeContent';
import { editorImageGuideFor } from '@/lib/upload/uploadGuide';

interface NoticeFormState {
  title: string;
  content: string;
  category: string;
  isPinned: boolean;
  status: NoticeStatus;
  publishedAt: string;
  // QA-122 — 로케일별 본문 HTML(en/ja/zh). ko-KR 은 content 정본. 빈 값 = SF ko 폴백.
  contentTranslations: Partial<Record<ContentLocale, string>>;
}

const EMPTY_FORM: NoticeFormState = {
  title: '',
  content: '',
  category: '',
  isPinned: false,
  status: 'DRAFT',
  publishedAt: '',
  contentTranslations: {},
};

// QA-122 — Notice translations 의 본문 필드 키는 'content'(NoticeTranslation.content).
const NOTICE_TRANSLATION_HTML_FIELD = 'content';

/** ISO 8601 (with offset/Z) → value for <input type="datetime-local"> ("YYYY-MM-DDTHH:mm"). */
const isoToLocalInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** datetime-local value → ISO 8601 string in local timezone offset. */
const localInputToIso = (local: string): string | undefined => {
  if (!local) return undefined;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

export const NoticeFormPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { noticeId: noticeIdParam } = useParams<{ noticeId: string }>();
  const isNew = noticeIdParam === 'new' || noticeIdParam == null;
  const noticeId = isNew ? null : Number(noticeIdParam);

  const query = useNoticeQuery(noticeId);
  const { create, update, remove } = useNoticeMutation();
  const uploadImage = useNoticeImageUpload();

  const [form, setForm] = useState<NoticeFormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!query.data) return;
    setForm({
      title: query.data.title,
      content: query.data.content,
      category: query.data.category ?? '',
      isPinned: query.data.isPinned,
      status: query.data.status,
      publishedAt: isoToLocalInput(query.data.publishedAt),
      contentTranslations: pickLocaleHtml(query.data.translations, NOTICE_TRANSLATION_HTML_FIELD),
    });
  }, [query.data]);

  const set = <K extends keyof NoticeFormState>(key: K, value: NoticeFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setContentTranslation = (locale: ContentLocale, html: string) =>
    setForm((prev) => ({
      ...prev,
      contentTranslations: { ...prev.contentTranslations, [locale]: html },
    }));

  const statusOptions = [
    { value: 'DRAFT', label: t('notice.status.DRAFT') },
    { value: 'PUBLISHED', label: t('notice.status.PUBLISHED') },
  ];

  const canSubmit = form.title.trim() !== '' && form.content.trim() !== '';

  const handleSubmit = async () => {
    setError(null);
    if (!canSubmit) {
      setError(t('notice.validation.required'));
      return;
    }
    const publishedAtIso = localInputToIso(form.publishedAt);
    const translations = buildLocaleHtmlTranslations<NoticeTranslation>(
      form.contentTranslations,
      NOTICE_TRANSLATION_HTML_FIELD,
    );
    const payload: NoticePayload = {
      title: form.title.trim(),
      content: form.content,
      isPinned: form.isPinned,
      status: form.status,
      ...(form.category.trim() ? { category: form.category.trim() } : {}),
      ...(publishedAtIso ? { publishedAt: publishedAtIso } : {}),
      ...(translations ? { translations } : {}),
    };
    try {
      if (isNew) {
        await create.mutateAsync(payload);
      } else if (noticeId != null) {
        await update.mutateAsync({ noticeId, payload });
      }
      navigate('/cms/notices');
    } catch (e) {
      setError(friendlyMessage(e, t('notice.saveFailed')));
    }
  };

  const handleDelete = async () => {
    if (noticeId == null) return;
    setError(null);
    try {
      await remove.mutateAsync(noticeId);
      navigate('/cms/notices');
    } catch (e) {
      setError(friendlyMessage(e, t('notice.deleteFailed')));
    }
  };

  const isBusy = create.isPending || update.isPending || remove.isPending;

  const breadcrumb = [
    { label: 'CMS', href: '/cms/notices' },
    { label: t('notice.title'), href: '/cms/notices' },
    { label: isNew ? t('notice.create') : t('notice.edit') },
  ];

  if (!isNew && query.isPending) {
    return (
      <PageShell title={t('notice.editTitle')} breadcrumb={breadcrumb}>
        <div className="space-y-3" role="status" aria-live="polite">
          <Skeleton className="h-[44px] w-full" />
          <Skeleton className="h-[200px] w-full" />
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isNew ? t('notice.createTitle') : t('notice.editTitle')}
      breadcrumb={breadcrumb}
      actions={
        <div className="flex gap-2">
          {!isNew && (
            <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)} disabled={isBusy}>
              {t('notice.delete')}
            </Button>
          )}
          <Button size="sm" onClick={handleSubmit} disabled={isBusy || !canSubmit}>
            {t('notice.save')}
          </Button>
        </div>
      }
    >
      {error && (
        <div className="rounded-md p-3 text-sm" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
          {error}
        </div>
      )}

      <SectionCard title={t('notice.sectionBasic')}>
        <div className="flex flex-col gap-3">
          <FormSection label={t('notice.fields.title')} density="compact" required>
            <Input
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              maxLength={200}
              required
            />
          </FormSection>
          <FormSection label={t('notice.fields.category')} density="compact">
            <Input
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              maxLength={32}
              placeholder={t('notice.fields.categoryPlaceholder')}
              className="max-w-[280px]"
            />
          </FormSection>
          <FormSection label={t('notice.fields.status')} density="compact">
            <RadioGroup
              value={form.status}
              onValueChange={(v) => set('status', v as NoticeStatus)}
              options={statusOptions}
              orientation="horizontal"
              aria-label={t('notice.fields.status')}
            />
          </FormSection>
          <FormSection label={t('notice.fields.publishedAt')} density="compact">
            <Input
              type="datetime-local"
              value={form.publishedAt}
              onChange={(e) => set('publishedAt', e.target.value)}
              className="max-w-[280px]"
            />
          </FormSection>
          <FormSection label={t('notice.fields.isPinned')} density="compact">
            <Switch
              checked={form.isPinned}
              onCheckedChange={(v) => set('isPinned', v)}
              aria-label={t('notice.fields.isPinned')}
            />
          </FormSection>
        </div>
      </SectionCard>

      {/* QA-56 — 본문(ko 정본)은 저장 검증(canSubmit)이 하드 강제하므로 필수 표기. */}
      <SectionCard title={<span>{t('notice.sectionContent')}<RequiredMark /></span>}>
        {/* QA-122 — 본문을 로케일(ko/en/ja/zh)별로 입력. ko 정본, 타 로케일 빈 값 = SF ko 폴백. */}
        <LocaleHtmlTabs
          baseValue={form.content}
          onBaseChange={(html) => set('content', html)}
          translations={form.contentTranslations}
          onTranslationChange={setContentTranslation}
          onUploadImage={uploadImage}
          imageGuide={editorImageGuideFor('NOTICE_BODY')}
          onUploadError={(e) => setError(friendlyMessage(e, t('notice.imageUploadFailed')))}
          ariaLabelPrefix={t('notice.fields.content')}
        />
      </SectionCard>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('notice.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('notice.deleteDesc', { title: form.title })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('notice.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={isBusy}
              onClick={() => { setConfirmDelete(false); void handleDelete(); }}
            >
              {t('notice.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
